import React, { useCallback, useState } from 'react';

import Confirmation from '../../../../components/confirmation/confirmation';
import FitmentSizeGroup from '../../../../components/fitment-size/components/fitment-size-group/fitment-size-group';
import FitmentSize from '../../../../components/fitment-size/fitment-size';
import VehicleImage from '../../../../components/vehicle-image/vehicle-image';
import {
  BellSolidIcon,
  ChevronRightRegularIcon,
  ShoppingCartSolidIcon,
  TrashAltSolidIcon
} from '../../../../shared/icons/icons';
import Button from '../../../button/button';
import Typography from '../../../typography/typography';
import VehicleDescription from '../../../vehicle-description/vehicle-description';
import VehicleLicenseVinDetails from '../../../vehicle-license-vin-details/vehicle-license-vin-details';

import {
  useCmsPageContentSlots,
  useFitmentConfirmation,
  useModal,
  useNavigateTo,
  useTireWheelPackage
} from '../../../../shared/hooks/hooks';

import { Fitment, MaxSize } from '../../../../shared/types/fitment';
import { VEHICLE_CONFIRMATION } from '../../../../shared/constants/modals';

import './fitment-banner.scss';

interface FitmentBannerProps {
  className?: string;
  fitment: Fitment;
  hasItemsInCart?: boolean;
  hideDelete?: boolean;
  hideFitmentSize?: boolean;
  hideIconInSelect?: boolean;
  hideRideHeight?: boolean;
  hideVehicleImage?: boolean;
  incompleteFitmentMessage?: string;
  isPartialFitment?: boolean;
  isSaveVehicleDisabled?: boolean;
  maxSize?: MaxSize;
  onEditFitment?: (fitment: Fitment) => void;
  onRemoveFitment?: (fitment: Fitment) => void;
  onSaveVehicle?: () => void;
  onSelectFitment?: (fitment: Fitment) => void;
  onShopProducts?: () => void;
  selectedFitment?: Fitment;
  showAnimation?: boolean;
  showChangeVehicleLink?: boolean;
  showEditLink?: boolean;
  showLicenseVin?: boolean;
}

function FitmentBanner(props: FitmentBannerProps) {
  const {
    className,
    fitment,
    hasItemsInCart,
    hideDelete = false,
    hideFitmentSize = false,
    hideIconInSelect = false,
    hideRideHeight = false,
    hideVehicleImage = false,
    incompleteFitmentMessage,
    isPartialFitment = false,
    isSaveVehicleDisabled = false,
    maxSize = '',
    onEditFitment,
    onRemoveFitment,
    onSaveVehicle,
    onSelectFitment,
    onShopProducts,
    selectedFitment,
    showAnimation,
    showChangeVehicleLink = false,
    showEditLink,
    showLicenseVin = false
  } = props;

  const { openModal } = useModal();
  const { getContentSlot } = useCmsPageContentSlots('fitment-messages');
  const { confirmCancelTireWheelPackage, incompletePackage } =
    useTireWheelPackage();
  const { promptRemoveFitmentConfirmation, removeFitment, selectFitment } =
    useFitmentConfirmation({
      fitment: {
        removeFitment: onRemoveFitment,
        selectFitment: onSelectFitment
      }
    });
  const navigateTo = useNavigateTo();

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const {
    frontTireSize,
    isStaggered,
    make,
    model,
    rearTireSize,
    rideHeight,
    rideHeightType,
    trim,
    year
  } = fitment || {};
  const hasStaggeredFitment = isStaggered && rearTireSize;
  const wrapperMaxSizeClassName = maxSize ? `wrapper--max-${maxSize}` : '';
  let containerStyleName = showAnimation ? 'animated-container' : 'container';
  const isSelectedFitment = selectedFitment?.uid === fitment?.uid;
  const wrapperSelectedClassName = isSelectedFitment ? 'wrapper--selected' : '';

  const handleChangeVehicle = useCallback(async () => {
    if (incompletePackage) {
      await confirmCancelTireWheelPackage(undefined, {
        onCancelPackage: () => {
          navigateTo(location.pathname.replace('/packages', ''));
        }
      });
    } else {
      openModal(VEHICLE_CONFIRMATION);
    }
  }, [confirmCancelTireWheelPackage, incompletePackage, navigateTo, openModal]);

  const handleConfirmDelete = useCallback(() => {
    setIsDeleteLoading(true);
    removeFitment(fitment);
  }, [fitment, removeFitment]);

  function toggleDeleteConfirmation() {
    promptRemoveFitmentConfirmation(fitment);
    setShowDeleteConfirmation(!showDeleteConfirmation);
  }

  function getPartialFitmentMessage() {
    if (incompleteFitmentMessage) return incompleteFitmentMessage;

    const slotKey = trim
      ? 'incomplete-original-tire-size'
      : 'incomplete-trim-and-original-tire-size';

    return (
      getContentSlot(slotKey) ||
      `Your vehicle information has an incomplete ${
        trim ? 'original tire size' : 'trim and original tire size'
      }`
    );
  }

  return (
    <div
      className={`fitment-banner ${className || ''}`}
      styleName={`wrapper ${wrapperMaxSizeClassName} ${wrapperSelectedClassName}`}
    >
      {showDeleteConfirmation && (
        <Confirmation
          confirmLabel="Yes, delete this vehicle"
          declineLabel="No, keep this vehicle"
          handleConfirm={handleConfirmDelete}
          handleDecline={toggleDeleteConfirmation}
          hasOverlay
          showSpinner={isDeleteLoading}
        />
      )}
      <div
        className={`fitment-banner__container ${
          className ? `${className}__container` : null
        }`}
        styleName={containerStyleName}
      >
        {!hideVehicleImage && (
          <div
            className="fitment-banner__vehicle-display"
            styleName="vehicle-display"
          >
            <VehicleImage
              alt="vehicle"
              className="fitment-banner__vehicle-image__vehicle-display"
              smallImageUrl={fitment?.smallImageUrl}
              styleName="image"
              vehicleType={fitment?.type}
            />
          </div>
        )}
        <div
          className="fitment-banner__vehicle-description"
          styleName={
            hasStaggeredFitment
              ? 'staggered-vehicle-size-details'
              : 'vehicle-size-details'
          }
        >
          <div
            className="fitment-banner__vehicle-description-size-container"
            styleName="vehicle-description-size-container"
          >
            <div
              className="fitment-banner__vehicle-description"
              styleName="vehicle-description"
            >
              <VehicleDescription
                make={make}
                model={model}
                renderAfterYearMake={() => (
                  <>
                    {showEditLink && (
                      <Button
                        className="fitment-banner__vehicle-description-edit-button"
                        onClick={() => onEditFitment?.(fitment)}
                        styleName={
                          hasStaggeredFitment
                            ? 'edit-vehicle-staggered'
                            : 'edit-vehicle'
                        }
                      >
                        edit
                      </Button>
                    )}
                  </>
                )}
                rideHeight={hideRideHeight ? undefined : rideHeight}
                trim={trim}
                year={year}
              />
            </div>

            {!hideFitmentSize && (
              <div
                className="fitment-banner__fitment-size"
                styleName={
                  hasStaggeredFitment
                    ? 'size-details-staggered'
                    : 'size-details'
                }
              >
                {frontTireSize && (
                  <FitmentSizeGroup
                    styleName={isPartialFitment ? 'partial-size-group' : ''}
                  >
                    <FitmentSize
                      isOE={fitment?.isOE}
                      rideHeightType={rideHeightType}
                      sizeQualifier={fitment?.sizeQualifier}
                      tireLocation={hasStaggeredFitment ? 'front' : void 0}
                      tireSize={frontTireSize}
                      wheelDiameter={fitment?.frontWheelDiameter}
                    />

                    {isStaggered &&
                      (Boolean(rearTireSize) ||
                        Boolean(fitment?.rearWheelDiameter)) && (
                        <FitmentSize
                          isOE={fitment?.isOE}
                          rideHeightType={rideHeightType}
                          sizeQualifier={fitment?.sizeQualifier}
                          tireLocation="rear"
                          tireSize={rearTireSize}
                          wheelDiameter={fitment?.rearWheelDiameter}
                        />
                      )}
                  </FitmentSizeGroup>
                )}
                {showLicenseVin && (
                  <Typography styleName="license-vin-details" tag="div">
                    <VehicleLicenseVinDetails
                      license={fitment?.licensePlate}
                      vin={fitment?.vin}
                    />
                  </Typography>
                )}
                {isPartialFitment && (
                  <Typography
                    size="extraSmall"
                    styleName="incomplete-fitment-message"
                    tag="div"
                  >
                    <BellSolidIcon />
                    {getPartialFitmentMessage()}
                  </Typography>
                )}
                {hasItemsInCart && (
                  <Typography styleName="products-in-cart" tag="div">
                    <ShoppingCartSolidIcon styleName="cart-icon" />
                    Products in cart
                  </Typography>
                )}
              </div>
            )}
          </div>

          <div
            className="fitment-banner__actions"
            styleName={isStaggered ? 'staggered-actions' : 'actions'}
          >
            {typeof onSelectFitment === 'function' &&
              typeof selectFitment === 'function' && (
                <Button onClick={() => selectFitment(fitment)}>
                  select vehicle{' '}
                  {!hideIconInSelect && <ChevronRightRegularIcon />}
                </Button>
              )}
            {showChangeVehicleLink && (
              <Button onClick={handleChangeVehicle} styleName="change-vehicle">
                change vehicle
              </Button>
            )}
            {typeof onShopProducts === 'function' && (
              <Button onClick={onShopProducts}>shop products</Button>
            )}
            {typeof onSaveVehicle === 'function' && (
              <Button
                isDisabled={isSaveVehicleDisabled}
                onClick={onSaveVehicle}
              >
                save vehicle
              </Button>
            )}
          </div>
        </div>
        {!hideDelete && (
          <Button
            data-testid="fitment-banner_delete-vehicle"
            icon={TrashAltSolidIcon}
            onClick={toggleDeleteConfirmation}
            styleName="delete"
          />
        )}
      </div>
    </div>
  );
}

export default FitmentBanner;
